import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Typography,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { CancelIcon } from '../../../vectors/index';
import Done from '@mui/icons-material/Done';
import { Employer } from '../types';
import { useCancelSubscription } from '../hooks/useCancelSubscription';
import SelectDateModal from '@components/SelectDateModal/SelectDateModal';
import { Dayjs } from 'dayjs';

type CancelServiceModalProps = {
  handleClose: () => void;
  item: Employer;
};

const CancelServiceModal: FC<CancelServiceModalProps> = ({
  handleClose,
  item,
}) => {
  const [errorText, setErrorText] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('immediately');
  const [date, setDate] = useState<Dayjs | string | null>(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);

  const { cancelSubscription, isCancelSubcription } = useCancelSubscription({
    setErrorText,
  });

  const handleCancel = () => {
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        handleClose();
      },
    };
    cancelSubscription(
      {
        ...(selectedOption === 'immediately'
          ? { immediately_or_end: true }
          : {}),
        ...(selectedOption === 'end_of_period'
          ? { immediately_or_end: false }
          : {}),
        user_id: item.company
          ? item.company.id
          : item.enterprise
            ? item.enterprise?.id
            : '',
        ...(selectedOption === 'custom_date'
          ? { time: date && typeof date !== 'string' ? date.format('DD-MM-YYYY') : '' }
          : {}),
        ...(item.enterprise ? { is_enterprise: true } : {}),
      },
      options,
    );
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <CancelIcon />
        <br />
        <Typography variant="h4" m={2}>
          Cancel Service
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert sx={{ margin: '10px 0' }} severity="error">
            {errorText}
          </Alert>
        )}
        {((item?.company && item?.company?.is_company_paid) || item.enterprise) ? (
          <>
            <Typography variant="h6" gutterBottom>
              When do you want to cancel?
            </Typography>
            <RadioGroup
              value={selectedOption}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                if (e.target.value === 'custom_date') {
                  setIsDateModalOpen(true);
                }
              }}
            >
              <FormControlLabel
                value="immediately"
                control={<Radio />}
                label="Immediately"
              />
              <FormControlLabel
                value="end_of_period"
                control={<Radio />}
                label="End of Current Period"
              />
              <FormControlLabel
                value="custom_date"
                control={<Radio />}
                label="On Custom Date"
              />
            </RadioGroup>
            {selectedOption === 'custom_date' && date && (
              <Typography variant="body1" gutterBottom>
                Selected date: {date && typeof date !== 'string' ? date.format('YYYY-MM-DD') : ''}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography textAlign="center" variant="h4" gutterBottom>
              Account is billed under an Enterprise plan.
            </Typography>
            <Typography textAlign="center" variant="h6" gutterBottom>
              Canceling now will immediately deactivate your subscription and
              associated services.
            </Typography>
          </>
        )}
        <Button
          onClick={handleClose}
          disabled={isCancelSubcription}
          color="info"
          startIcon={<CloseIcon />}
          fullWidth
          variant="outlined"
          sx={{ margin: '10px 0' }}
        >
          Don't Cancel
        </Button>
        <LoadingButton
          fullWidth
          loading={isCancelSubcription}
          disabled={isCancelSubcription}
          startIcon={<Done />}
          sx={{ marginBottom: 2 }}
          variant="contained"
          color="primary"
          onClick={handleCancel}
        >
          Cancel Service
        </LoadingButton>
        <SelectDateModal
          isOpen={isDateModalOpen}
          handleClose={() => setIsDateModalOpen(false)}
          date={date}
          setDate={setDate}
          disablePast
        />
      </DialogContent>
    </Dialog>
  );
};

export default CancelServiceModal;
